import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, EventsService, HelperService } from "@mypxplat/xplat/core";

/**
 * Pre-show countdown for a livestream event: background art, event name + local start time,
 * DD:HH:MM:SS timer, capped description, then a final-15-seconds giant-digit takeover and a
 * brief LIVE flash before handing off to the player.
 *
 * Shared by the fender.com embed (fui-embed-livestream) and the in-app video player, which both
 * render it until `wentLive` fires and then swap in the actual stream. Owns all of its own
 * timing so callers only need to hand it an event and react to the outputs.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-livestream-countdown",
  templateUrl: "fui-livestream-countdown.component.html",
  standalone: false,
})
export class FuiLivestreamCountdownComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() event: any = null;

  /**
   * Sign-up CTA (DENG-892/893): when on, a "Sign Up" button opens the email-capture form
   * (`fui-embed-signup`) in a modal, styled like the description modal.
   */
  @Input() signupEnabled: boolean = false;
  /**
   * Per-state sign-up config for the "countdown" state (button_text / form_message / success_message);
   * list_id + source stay server-side. Drives the CTA label and is handed to fui-embed-signup.
   */
  @Input() signupConfig: any = null;
  /** Event id this sign-up belongs to (sent to the capture endpoint). */
  @Input() signupEventId: string = "";

  /**
   * Trailer/preview YouTube id override from the `?previewVideo=` URL param (embed). When absent, the
   * id is read from the event's `preview_video_id` config. Either way, a "Watch The Trailer" button
   * appears next to Sign Up and opens the video in a modal.
   */
  @Input() previewVideoId: string = "";
  /** Open the trailer modal immediately on load (set when the player was launched via "Watch Trailer"). */
  @Input() autoOpenTrailer: boolean = false;
  /** Resolved trailer id (URL-param override, else the event config); drives the CTA + modal. */
  public resolvedPreviewVideoId: string = "";

  /** Fires the moment the event's start time is reached (after the LIVE flash, if one played). */
  @Output() wentLive: EventEmitter<any> = new EventEmitter();

  /** Fires when the event's end time passes while the countdown is still on screen. */
  @Output() ended: EventEmitter<any> = new EventEmitter();

  public htmlDescription: string = "";
  public descriptionOverflows: boolean = false;
  public showDescriptionModal: boolean = false;
  public showSignupModal: boolean = false;
  public showPreviewModal: boolean = false;
  /** Built from `previewVideoId` in ngOnInit; empty when no preview is configured. */
  public previewEmbedUrl: string = "";
  public localDateTime: string = "";

  @ViewChild("descText") descText?: ElementRef<HTMLElement>;

  public countdownDays: number = 0;
  public countdownHours: number = 0;
  public countdownMinutes: number = 0;
  public countdownSeconds: number = 0;
  public showCountdown: boolean = false;
  public eventEnded: boolean = false;

  /**
   * The last-15-seconds "big number" takeover. Replaces the title/description/date/normal timer
   * with a single giant countdown digit. `finalCountdownNumber` is the number to render (15..1);
   * when we hit 0 we briefly show `showingLiveFlash` before handing off to the player.
   */
  public finalCountdownActive: boolean = false;
  public finalCountdownNumber: number = 0;
  public showingLiveFlash: boolean = false;
  private static readonly FINAL_COUNTDOWN_THRESHOLD_SECS = 15;
  private static readonly LIVE_FLASH_MS = 500;
  private liveFlashTimer: any = null;

  private countdownInterval: any;

  constructor(
    userService: UserService,
    public eventsService: EventsService,
    private helperService: HelperService
  ) {
    super(userService);
  }

  /** Sign-up CTA → open the email-capture form in a modal (styled like the description modal). */
  openSignup() {
    this.showSignupModal = true;
  }

  closeSignup() {
    this.showSignupModal = false;
  }

  /** "Watch The Trailer" → open the preview video in a modal. */
  openPreview() {
    this.showPreviewModal = true;
  }

  closePreview() {
    this.showPreviewModal = false;
  }

  ngOnInit() {
    if (!this.event) return;
    this.buildDescription();
    this.buildLocalDateTime();
    // Trailer id: the ?previewVideo= URL param (embed) wins; otherwise fall back to the event config.
    this.resolvedPreviewVideoId = this.previewVideoId || this.event?.preview_video_id || "";
    // autoplay only kicks in once the viewer opens the modal (the iframe isn't in the DOM before).
    if (this.resolvedPreviewVideoId) {
      this.previewEmbedUrl = `https://www.youtube.com/embed/${encodeURIComponent(this.resolvedPreviewVideoId)}?autoplay=1&rel=0&modestbranding=1`;
      // Launched via "Watch Trailer": open the trailer modal straight away.
      if (this.autoOpenTrailer) this.openPreview();
    }
    this.startCountdown();
    // Measure after the countdown view (which hosts the description) has rendered.
    setTimeout(() => this.measureDescriptionOverflow(), 0);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    if (this.liveFlashTimer) clearTimeout(this.liveFlashTimer);
  }

  @HostListener("window:resize")
  onResize() {
    this.measureDescriptionOverflow();
  }

  /**
   * Escape closes the open modal (the sign-up form isn't closable by backdrop click). The country
   * combobox stops propagation on Escape while its list is open, so the first Escape closes the list
   * and a second closes the modal.
   */
  @HostListener("document:keydown.escape")
  onEscapeKey() {
    if (this.showSignupModal) this.closeSignup();
    else if (this.showPreviewModal) this.closePreview();
    else if (this.showDescriptionModal) this.closeDescription();
  }

  openDescription() {
    this.showDescriptionModal = true;
  }

  closeDescription() {
    this.showDescriptionModal = false;
  }

  private buildDescription() {
    const desc = this.event?.description;
    if (!desc) {
      this.htmlDescription = "";
      return;
    }
    const rawHtml = this.helperService.containsHtml(desc) ? desc : this.helperService.makeHtml(desc);
    this.htmlDescription = this.helperService.sanitizeQuillHtml(rawHtml);
  }

  /** Format the event start in the viewer's local timezone (e.g. "Fri, Aug 1 · 5:00 PM PDT"). */
  private buildLocalDateTime() {
    const start = this.eventsService.getEventStartMs(this.event);
    if (!isFinite(start)) {
      this.localDateTime = "";
      return;
    }
    const startDate = new Date(start);
    const date = startDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const time = startDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
    this.localDateTime = `${date} · ${time}`;
  }

  private measureDescriptionOverflow() {
    const el = this.descText?.nativeElement;
    if (!el) return;
    this.descriptionOverflows = el.scrollHeight - el.clientHeight > 2;
  }

  private startCountdown() {
    const startTime = this.eventsService.getEventStartMs(this.event);
    const endTime = this.eventsService.getEventEndMs(this.event);
    if (!isFinite(startTime) || !isFinite(endTime)) return;

    const tick = () => {
      const now = Date.now();
      if (now >= startTime && now <= endTime) {
        // Reached zero. If the final-15 takeover was running, hand off through a brief LIVE
        // flash so the player doesn't just pop in mid-countdown. Otherwise (mounted during the
        // event) tell the parent immediately.
        this.showCountdown = false;
        this.eventEnded = false;
        if (this.countdownInterval) clearInterval(this.countdownInterval);
        if (this.finalCountdownActive) {
          this.finalCountdownActive = false;
          this.showingLiveFlash = true;
          this.liveFlashTimer = setTimeout(() => {
            this.showingLiveFlash = false;
            this.wentLive.emit(this.event);
          }, FuiLivestreamCountdownComponent.LIVE_FLASH_MS);
        } else {
          // Deferred: the very first tick runs inside ngOnInit, and emitting synchronously there
          // would mutate the parent mid-change-detection.
          setTimeout(() => this.wentLive.emit(this.event), 0);
        }
        return;
      }
      if (now > endTime) {
        this.showCountdown = false;
        this.eventEnded = true;
        this.finalCountdownActive = false;
        if (this.countdownInterval) clearInterval(this.countdownInterval);
        setTimeout(() => this.ended.emit(this.event), 0);
        return;
      }
      const diff = startTime - now;
      const totalSecs = Math.floor(diff / 1000);
      // Under the threshold, take over the UI with the giant single digit.
      if (totalSecs <= FuiLivestreamCountdownComponent.FINAL_COUNTDOWN_THRESHOLD_SECS) {
        this.showCountdown = false;
        this.finalCountdownActive = true;
        // Show 1 during the last second, 15 at 15s, etc. `totalSecs` already floors partial
        // seconds, so at the moment we cross into "14 seconds left" it reads 14 not 15.
        this.finalCountdownNumber = Math.max(1, totalSecs);
      } else {
        this.showCountdown = true;
        this.finalCountdownActive = false;
        this.countdownDays = Math.floor(diff / 86400000);
        this.countdownHours = Math.floor((diff % 86400000) / 3600000);
        this.countdownMinutes = Math.floor((diff % 3600000) / 60000);
        this.countdownSeconds = totalSecs % 60;
      }
    };

    tick();
    this.countdownInterval = setInterval(tick, 1000);
  }
}
